/**
 * Qualification Lists Query Hooks (Epic C-2 — Kwalificaties, Story C-2.1)
 *
 * Eigen bestand, zelfde query-keys-factory + invalidatie-patroon als
 * `usePlanningTasks.ts` (AD-9/AD-18).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qualificationsApi, type QualificationListKind } from '../../../lib/qualificationsApi';
import type { CreateQualificationListItemInput, UpdateQualificationListItemInput } from '@functieloonhuis/shared';

export const qualificationListKeys = {
  all: (kind: QualificationListKind) => ['qualificationLists', kind] as const,
  lists: (kind: QualificationListKind) => [...qualificationListKeys.all(kind), 'list'] as const,
  list: (kind: QualificationListKind, includeArchived: boolean) => [...qualificationListKeys.lists(kind), { includeArchived }] as const,
};

export function useQualificationListItems(kind: QualificationListKind, includeArchived = false) {
  return useQuery({
    queryKey: qualificationListKeys.list(kind, includeArchived),
    queryFn: () => qualificationsApi.listItems(kind, includeArchived),
  });
}

export function useCreateListItem(kind: QualificationListKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateQualificationListItemInput) => qualificationsApi.createItem(kind, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationListKeys.lists(kind) });
    },
  });
}

export function useUpdateListItem(kind: QualificationListKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateQualificationListItemInput }) => qualificationsApi.updateItem(kind, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationListKeys.lists(kind) });
    },
  });
}

export function useArchiveListItem(kind: QualificationListKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => qualificationsApi.archiveItem(kind, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationListKeys.lists(kind) });
    },
  });
}

export function useRestoreListItem(kind: QualificationListKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => qualificationsApi.restoreItem(kind, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualificationListKeys.lists(kind) });
    },
  });
}
